import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  Users,
  Wrench,
  Calendar,
  Award,
  Target,
  Activity,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Filter,
  Download,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const StatsManager = () => {
  const [loading, setLoading] = useState(true);
  const [interventions, setInterventions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    thisMonth: 0,
    thisWeek: 0,
    mechanical: 0,
    bodywork: 0,
    uniqueApprentices: 0,
    totalCompetences: 0,
    avgCompetencesPerIntervention: 0
  });
  const [dateFilter, setDateFilter] = useState('all'); // all, week, month, year
  const [formationFilter, setFormationFilter] = useState('all');

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  useEffect(() => {
    loadData();
  }, [dateFilter, formationFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('📊 [StatsManager] Chargement des données...');
      
      // ✅ Requête simplifiée : juste les interventions
      let query = supabase
        .from('interventions')
        .select('*')
        .order('created_at', { ascending: false });

      // Filtrer par date
      if (dateFilter !== 'all') {
        const now = new Date();
        let startDate;

        switch (dateFilter) {
          case 'week':
            startDate = new Date(now.setDate(now.getDate() - 7));
            break;
          case 'month':
            startDate = new Date(now.setMonth(now.getMonth() - 1));
            break;
          case 'year':
            startDate = new Date(now.setFullYear(now.getFullYear() - 1));
            break;
          default:
            startDate = null;
        }

        if (startDate) {
          query = query.gte('created_at', startDate.toISOString());
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Erreur Supabase:', error);
        throw error;
      }

      console.log('✅ Interventions chargées:', data?.length || 0);
      setInterventions(data || []);
      calculateStats(data || []);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    console.log('📈 Calcul des statistiques pour', data.length, 'interventions');
    
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.setMonth(now.getMonth() - 1));

    const thisWeek = data.filter(
      (i) => new Date(i.created_at) >= oneWeekAgo
    ).length;

    const thisMonth = data.filter(
      (i) => new Date(i.created_at) >= oneMonthAgo
    ).length;

    const mechanical = data.filter((i) => i.intervention_type === 'mechanical').length;
    const bodywork = data.filter((i) => i.intervention_type === 'bodywork').length;

    // ✅ Compter les apprenants uniques
    const apprenticeIds = new Set();
    data.forEach((intervention) => {
      // Gérer format apprentices (array ou JSON)
      let apprentices = intervention.apprentices || [];
      
      if (typeof apprentices === 'string') {
        try {
          apprentices = JSON.parse(apprentices);
        } catch (e) {
          apprentices = [];
        }
      }
      
      apprentices.forEach((app) => {
        if (app.apprenantId) {
          apprenticeIds.add(app.apprenantId);
        }
      });
    });

    // ✅ Compter les compétences
    let totalCompetences = 0;
    data.forEach((intervention) => {
      // Gérer format apprentices
      let apprentices = intervention.apprentices || [];
      
      if (typeof apprentices === 'string') {
        try {
          apprentices = JSON.parse(apprentices);
        } catch (e) {
          apprentices = [];
        }
      }
      
      // Compter compétences dans chaque apprenant
      apprentices.forEach((app) => {
        const competences = app.competences || [];
        totalCompetences += competences.length;
      });
    });

    const calculatedStats = {
      total: data.length,
      thisMonth,
      thisWeek,
      mechanical,
      bodywork,
      uniqueApprentices: apprenticeIds.size,
      totalCompetences,
      avgCompetencesPerIntervention:
        data.length > 0 ? (totalCompetences / data.length).toFixed(1) : 0,
    };
    
    console.log('📊 Stats calculées:', calculatedStats);
    setStats(calculatedStats);
  };

  // Statistiques par formation
  const getFormationStats = () => {
    const formationCounts = {};

    interventions.forEach((intervention) => {
      let apprentices = intervention.apprentices || [];
      
      if (typeof apprentices === 'string') {
        try {
          apprentices = JSON.parse(apprentices);
        } catch (e) {
          apprentices = [];
        }
      }
      
      apprentices.forEach((app) => {
        const groupe = app.groupe || 'Non défini';
        formationCounts[groupe] = (formationCounts[groupe] || 0) + 1;
      });
    });

    return Object.entries(formationCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10
  };

  // Évolution des interventions par mois
  const getMonthlyTrend = () => {
    const monthCounts = {};

    interventions.forEach((intervention) => {
      const date = new Date(intervention.created_at);
      const monthKey = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0');
      monthCounts[monthKey] = (monthCounts[monthKey] || 0) + 1;
    });

    return Object.entries(monthCounts)
      .map(([month, count]) => ({ month, count }))
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-6); // 6 derniers mois
  };

  // Top des compétences travaillées
  const getTopCompetences = () => {
    const competenceCounts = {};

    interventions.forEach((intervention) => {
      let apprentices = intervention.apprentices || [];
      
      if (typeof apprentices === 'string') {
        try {
          apprentices = JSON.parse(apprentices);
        } catch (e) {
          apprentices = [];
        }
      }
      
      apprentices.forEach((app) => {
        const competences = app.competences || [];
        competences.forEach((comp) => {
          const key = comp.titre || comp.code || 'Compétence inconnue';
          competenceCounts[key] = (competenceCounts[key] || 0) + 1;
        });
      });
    });

    return Object.entries(competenceCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-400">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  const formationStats = getFormationStats();
  const monthlyTrend = getMonthlyTrend();
  const topCompetences = getTopCompetences();

  return (
    <div className="p-6 space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="w-8 h-8" />
            Tableau de Bord
          </h1>
          <p className="text-gray-400 mt-1">
            Vue d'ensemble des interventions pédagogiques
          </p>
        </div>

        {/* Filtres */}
        <div className="flex items-center gap-3">
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="bg-slate-800 text-white px-4 py-2 rounded-lg border border-slate-700 focus:outline-none focus:border-blue-500"
          >
            <option value="all">Toutes les périodes</option>
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
          </select>

          <button
            onClick={loadData}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Cartes de statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total interventions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-xl p-6 border border-blue-700/50"
        >
          <div className="flex items-center justify-between mb-4">
            <FileText className="w-8 h-8 text-blue-400" />
            <TrendingUp className="w-6 h-6 text-green-400" />
          </div>
          <h3 className="text-gray-400 text-sm mb-1">Interventions Totales</h3>
          <p className="text-3xl font-bold text-white">{stats.total}</p>
          <p className="text-sm text-gray-400 mt-2">
            {stats.thisWeek} cette semaine
          </p>
        </motion.div>

        {/* Apprenants uniques */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-xl p-6 border border-green-700/50"
        >
          <div className="flex items-center justify-between mb-4">
            <Users className="w-8 h-8 text-green-400" />
            <Activity className="w-6 h-6 text-blue-400" />
          </div>
          <h3 className="text-gray-400 text-sm mb-1">Apprenants</h3>
          <p className="text-3xl font-bold text-white">{stats.uniqueApprentices}</p>
          <p className="text-sm text-gray-400 mt-2">Différents apprenants</p>
        </motion.div>

        {/* Type mécanique */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 rounded-xl p-6 border border-purple-700/50"
        >
          <div className="flex items-center justify-between mb-4">
            <Wrench className="w-8 h-8 text-purple-400" />
            <Target className="w-6 h-6 text-yellow-400" />
          </div>
          <h3 className="text-gray-400 text-sm mb-1">Mécanique</h3>
          <p className="text-3xl font-bold text-white">{stats.mechanical}</p>
          <p className="text-sm text-gray-400 mt-2">
            {stats.total > 0 ? Math.round((stats.mechanical / stats.total) * 100) : 0}% du total
          </p>
        </motion.div>

        {/* Compétences moyennes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-yellow-900/50 to-yellow-800/30 rounded-xl p-6 border border-yellow-700/50"
        >
          <div className="flex items-center justify-between mb-4">
            <Award className="w-8 h-8 text-yellow-400" />
            <CheckCircle className="w-6 h-6 text-green-400" />
          </div>
          <h3 className="text-gray-400 text-sm mb-1">Compétences</h3>
          <p className="text-3xl font-bold text-white">{stats.totalCompetences}</p>
          <p className="text-sm text-gray-400 mt-2">
            {stats.avgCompetencesPerIntervention} par intervention
          </p>
        </motion.div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Évolution mensuelle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/50 rounded-xl p-6 border border-slate-700"
        >
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            Évolution des Interventions
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#3B82F6"
                strokeWidth={2}
                name="Interventions"
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Répartition par formation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-800/50 rounded-xl p-6 border border-slate-700"
        >
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-green-400" />
            Répartition par Formation
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={formationStats}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {formationStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Top compétences */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-slate-800/50 rounded-xl p-6 border border-slate-700"
      >
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-400" />
          Top 10 des Compétences Travaillées
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={topCompetences}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey="name"
              stroke="#9CA3AF"
              angle={-45}
              textAnchor="end"
              height={120}
            />
            <YAxis stroke="#9CA3AF" />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1F2937',
                border: '1px solid #374151',
                borderRadius: '8px',
              }}
            />
            <Bar dataKey="value" fill="#3B82F6" name="Nombre d'interventions" />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Message si pas de données */}
      {stats.total === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-yellow-900/20 border border-yellow-700/50 rounded-xl p-6 text-center"
        >
          <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            Aucune intervention trouvée
          </h3>
          <p className="text-gray-400">
            Créez votre première intervention pour voir les statistiques
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default StatsManager;